import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { Hotel, CreditCard, DollarSign, Star, User, CheckCircle, AlertTriangle, Info, Compass, Plane } from 'lucide-react'
import cartService from '../services/cartService'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AddCardModal from '../components/AddCardModal'
import ClientRegistrationModal from '../components/ClientRegistrationModal'
import CartReviewStep from '../components/CartReviewStep'
import PaymentStep from '../components/PaymentStep'
import clientService from '../services/clientService'
import { toast } from 'react-hot-toast'
import { motion } from 'framer-motion'
import checkoutService from '../services/checkoutService'
import experienceCheckoutService from '../services/experienceCheckoutService'
import stripePromise from '../config/stripe'
import { Elements } from '@stripe/react-stripe-js'
import MainLayout from '../components/Layout/MainLayout'
import { getHotelCartLinePayable } from '../utils/agentCommissionCart'
import { useCart } from '../contexts/CartContext'
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'
import PromoCodeApply from '../components/checkout/PromoCodeApply'

const QA_PROMO_UI_ENABLED = import.meta.env.VITE_ENABLE_STRIPE_MIN_QA_PROMO_UI === 'true'

const UserCartPage = () => {
  const { refreshCartCount, clearCartCount } = useCart()
  const [profile, setProfile] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [agentMarkupSettings, setAgentMarkupSettings] = useState({
    loaded: false,
    markupEnabled: false,
    markupPercentage: 10
  })
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const navigate = useNavigate()
  const location = useLocation()
  const [deleteCartItemLoading, setDeleteCartItemLoading] = useState(false)
  const [deleteAllCartDeleteLoading, setAllCartDeleteLoading] = useState(false)
  const [userPaymentDetails, setUserPaymentDetails] = useState(null)
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [currentStep, setCurrentStep] = useState(1) // 1: Cart Review, 2: Payment
  const [agentCustomerDetails, setAgentCustomerDetails] = useState([])
  const [showClientRegistration, setShowClientRegistration] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [selectedCustomerForItem, setSelectedCustomerForItem] = useState({})
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [checkoutFlowPreview, setCheckoutFlowPreview] = useState(null)
  const [stripeIntentPreview, setStripeIntentPreview] = useState(null)
  const [qaStripeMinPromoCode, setQaStripeMinPromoCode] = useState(null)
  const [qaPromoPreview, setQaPromoPreview] = useState(null)
  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    state: '',
    city: '',
    country: '',
    specialRequests: ''
  })
  const [useNewCard, setUseNewCard] = useState(false)
  const [newCardDetails, setNewCardDetails] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    cardType: ''
  })
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    countryCode: 'US'
  })
  const [expiryError, setExpiryError] = useState('')
  const [expiryInput, setExpiryInput] = useState('')

  const normalizePaymentMethod = (pm = {}) => {
    const id = pm._id || pm.id || pm.cardId || pm.stripePaymentMethodId
    return {
      ...pm,
      _id: pm._id || id,
      id: pm.id || id,
      stripePaymentMethodId: pm.stripePaymentMethodId || pm.cardId || null,
      stripeCustomerId: pm.stripeCustomerId || null,
      isStripeBased: !!(pm.stripePaymentMethodId || pm.cardId),
      brand: pm.brand || pm.cardType || 'card',
      last4: pm.last4 || '****',
      expMonth: pm.expMonth || pm.expiryMonth || '',
      expYear: pm.expYear || pm.expiryYear || '',
      funding: pm.funding || pm.cardType || 'card'
    }
  }

  const loadPreferredPaymentMethods = async () => {
    let stripeMethods = []
    try {
      const stripeRes = await travelAgentService.getStripePaymentMethods()
      stripeMethods = (stripeRes?.paymentMethods || []).map(normalizePaymentMethod)
    } catch (error) {
      console.warn('Stripe payment methods unavailable:', error)
    }

    const seen = new Set()
    const dedupedStripeMethods = stripeMethods.filter((pm) => {
      const key = pm.id || pm._id || pm.stripePaymentMethodId
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })

    setUserPaymentDetails(dedupedStripeMethods)
    if (dedupedStripeMethods.length > 0) {
      setUseNewCard(false)
      setSelectedPaymentMethod(dedupedStripeMethods[0])
    }
  }

  useEffect(() => {
    loadCartDetails()
    loadClientDetails()
    loadExperienceClientSelections()
  }, [])


  const loadCartDetails = async () => {
    try {
      const [responseUser, responseProfile] = await Promise.allSettled([
        cartService.getUserCart(),
        travelAgentService.getProfile()
      ])

      if (responseUser.status === 'fulfilled') {
        const cartItems = responseUser.value.data || []
        setProfile(cartItems)
        const unavailableHotels = cartItems.filter(
          (item) => item?.type !== 'experience' && item?.availabilityStatus?.isAvailable === false
        )
        if (unavailableHotels.length > 0) {
          console.warn('[UserCartPage] Unavailable hotel items found', unavailableHotels.map((item) => ({
            itemId: item?.id,
            hotelId: item?.hotelId,
            reason: item?.availabilityStatus?.reason,
            rateKeyPreview: String(item?.rateKey || '').slice(0, 24)
          })))
        }
        if (unavailableHotels.length > 0) {
          toast.error(
            `${unavailableHotels.length} hotel item${unavailableHotels.length > 1 ? 's are' : ' is'} no longer available. Remove and rebook.`
          )
        }
      }
      if (responseProfile.status === 'fulfilled') {
        const d = responseProfile.value?.data
        if (d) {
          const raw = d.markupPercentage
          const n = raw === null || raw === undefined ? 10 : Number(raw)
          const pct = Number.isNaN(n) ? 10 : Math.min(100, Math.max(10, n))
          setAgentMarkupSettings({
            loaded: true,
            markupEnabled: d.markupEnabled !== false,
            markupPercentage: pct
          })
        }
      }

      await loadPreferredPaymentMethods()

    } catch (error) {
      console.error('Failed to load profile:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load profile' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadClientDetails = async () => {
    try {
      const customers = await clientService.getAgentClients()
      setAgentCustomerDetails(customers.data || [])
    } catch (error) {
      console.error('Failed to load customers:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load customers' })
    }
  }

  const loadExperienceClientSelections = () => {
    // Load any saved client selections (both experience and hotel) from sessionStorage
    const keys = Object.keys(sessionStorage)
    const clientKeys = keys.filter(key => key.startsWith('selectedClient_'))

    const loadedSelections = {}
    clientKeys.forEach(key => {
      try {
        const data = JSON.parse(sessionStorage.getItem(key))
        // Extract cartItemId from key (format: selectedClient_{cartItemId})
        const cartItemId = key.replace('selectedClient_', '')

        if (data && cartItemId) {
          loadedSelections[cartItemId] = {
            id: data._id || data.id,
            ...data
          }
        }
      } catch (error) {
        console.error('Error loading client selection:', error)
      }
    })

    if (Object.keys(loadedSelections).length > 0) {
      setSelectedCustomerForItem(prev => ({ ...prev, ...loadedSelections }))
    }
  }

  const handleRegisterClient = async (clientData) => {
    setIsRegistering(true)
    try {
      const newClient = await clientService.createNewClient(clientData)
      setMessage({ type: 'success', text: 'Client registered successfully!' })
      toast.success(`Client registered successfully!`)

      await loadClientDetails()
      return newClient.data

    } catch (error) {
      toast.error('Failed to register client')
      setMessage({ type: 'error', text: error.message || 'Failed to register client' })
      throw error
    } finally {
      setIsRegistering(false)
    }
  }

  const handleCardAdded = async () => {
    try {
      await loadPreferredPaymentMethods()

      setMessage({ type: 'success', text: 'Payment method added successfully!' })
    } catch (error) {
      console.error('Failed to reload payment methods:', error)
      setMessage({ type: 'error', text: 'Failed to reload payment methods' })
    }
  }

  const getHotelChargedDisplay = (item) => {
    if (!qaPromoPreview?.perLine || qaPromoPreview.checkoutType !== 'hotel') {
      return Number(getHotelCartLinePayable(item) || 0)
    }
    const row = qaPromoPreview.perLine.find((p) => String(p.cartItemId) === String(item.id))
    return row != null ? Number(row.chargedAmount) : Number(getHotelCartLinePayable(item) || 0)
  }

  const calculateSummary = () => {
    if (!profile || profile.length === 0) return null

    let subtotal = 0
    let tax = 0
    let fees = 0
    let total = 0
    let markupTotal = 0
    let currency = profile[0]?.currency || ''

    const getItemProcessingFees = (item) => {
      // Prefer server-calculated Stripe fee snapshot if present.
      const explicit =
        Number(
          item?.processingFees?.totalProcessingFees ??
            item?.priceDetails?.processingFees?.totalProcessingFees ??
            item?.rateDetails?.processingFees?.totalProcessingFees ??
            item?.priceDetails?.additionalFees
        ) || 0
      // Always compute a derived value too, so we can detect stale snapshots.
      const payable = Number(getHotelCartLinePayable(item) || 0)
      const base = Number(item?.priceDetails?.baseRate || 0)
      const taxAmount = Number(item?.priceDetails?.tax || 0)
      const markup = Number(item?.agentCommission?.markupAmount || 0)
      const derived = payable - base - taxAmount - markup

      // If explicit snapshot exists but doesn't match totals (e.g. older cart items), trust derived.
      if (Number.isFinite(explicit) && explicit > 0) {
        if (Number.isFinite(derived) && derived > 0 && Math.abs(derived - explicit) > 0.02) {
          return derived
        }
        return explicit
      }

      // Fallback: derive fees from persisted totals (total = base + tax + markup + fees).
      return Number.isFinite(derived) && derived > 0 ? derived : 0
    }

    profile.forEach((item) => {
      subtotal += item.priceDetails?.baseRate || 0
      tax += item.priceDetails?.tax || 0
      fees += getItemProcessingFees(item)
      total += item.type === 'experience'
        ? Number(getHotelCartLinePayable(item) || 0)
        : getHotelChargedDisplay(item)
      if (item.type !== 'experience') {
        markupTotal += item.agentCommission?.markupAmount || 0
      }
    })

    return { subtotal, tax, fees, total, currency, markupTotal }
  }

  const handleQaPromoApplied = async (code) => {
    const res = await checkoutService.validateQaStripeMinPromo({
      checkoutType: 'hotel',
      qaStripeMinPromoCode: code
    })
    if (!res?.success || !res?.data) {
      throw new Error(res?.message || 'Could not apply promo code')
    }
    setQaStripeMinPromoCode(code)
    setQaPromoPreview(res.data)
    toast.success('QA promo applied')
  }

  const handleQaPromoCleared = () => {
    setQaStripeMinPromoCode(null)
    setQaPromoPreview(null)
  }

  const handleDeleteItem = async (cartItemId) => {
    try {
      setDeleteCartItemLoading(true)
      await cartService.deleteCartItem(cartItemId)
      refreshCartCount()
      setProfile((prev) => prev.filter((item) => item.id !== cartItemId))

      const newSelections = { ...selectedCustomerForItem }
      delete newSelections[cartItemId]
      setSelectedCustomerForItem(newSelections)

      // Remove experience client selection from sessionStorage if it exists
      sessionStorage.removeItem(`experience_client_${cartItemId}`)

      setMessage({ type: 'success', text: 'Item removed from cart' })
    } catch (error) {
      console.error('Failed to delete item:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to delete item' })
    } finally {
      setDeleteCartItemLoading(false)
    }
  }

  const handleClearAll = async () => {
    try {
      setAllCartDeleteLoading(true)
      await cartService.deleteAllCartItems()
      clearCartCount()
      setProfile([])
      setSelectedCustomerForItem({})

      // Clear experience client selections from sessionStorage
      const keys = Object.keys(sessionStorage)
      keys.filter(key => key.startsWith('experience_client_')).forEach(key => {
        sessionStorage.removeItem(key)
      })

      setMessage({ type: 'success', text: 'All items removed from cart' })
    } catch (error) {
      console.error('Failed to clear cart:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to clear cart' })
    } finally {
      setAllCartDeleteLoading(false)
    }
  }

  const handlePaymentMethodSelect = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod)
    setUseNewCard(false)
  }

  const handleDeleteSavedPaymentMethod = async (paymentMethod) => {
    try {
      const methodId =
        paymentMethod?.id ||
        paymentMethod?._id ||
        paymentMethod?.stripePaymentMethodId

      if (!methodId) {
        throw new Error('Payment method id is missing')
      }

      await experienceCheckoutService.removeStripePaymentMethod(methodId)

      const currentSelectedId = selectedPaymentMethod?.id || selectedPaymentMethod?._id
      const deletingId = paymentMethod?.id || paymentMethod?._id
      if (currentSelectedId && deletingId && currentSelectedId === deletingId) {
        setSelectedPaymentMethod(null)
      }

      await loadPreferredPaymentMethods()
      toast.success('Saved card removed')
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to remove saved card' })
      toast.error(error.message || 'Failed to remove saved card')
    }
  }

  const handleCustomerSelect = (itemId, customerId, customerData) => {
    setSelectedCustomerForItem(prev => ({
      ...prev,
      [itemId]: { id: customerId, ...customerData }
    }))
  }

  useEffect(() => {
    const resolveFlowPreviewForCart = async () => {
      if (!Array.isArray(profile) || profile.length === 0) {
        setCheckoutFlowPreview(null)
        return
      }

      const hotelItems = profile.filter((item) => item.type !== 'experience')
      if (hotelItems.length === 0) {
        setCheckoutFlowPreview(null)
        return
      }

      try {
        const resolved = await checkoutService.resolveHotelCheckoutFlow({
          cartItems: hotelItems.map((item) => ({ ...item, cartItemId: item.id })),
          ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
        })
        if (resolved?.success && resolved?.data) {
          setCheckoutFlowPreview(resolved.data)
        }
      } catch (error) {
        console.warn('Could not resolve checkout flow preview for cart items:', error)
      }
    }

    resolveFlowPreviewForCart()
  }, [profile, qaStripeMinPromoCode])

  const handleNextStep = () => {
    if (profile && profile.length > 0) {
      const unavailableHotels = profile.filter(
        (item) => item?.type !== 'experience' && item?.availabilityStatus?.isAvailable === false
      )
      if (unavailableHotels.length > 0) {
        console.warn('[UserCartPage] Blocking next step due to unavailable hotels', unavailableHotels.map((item) => ({
          itemId: item?.id,
          hotelId: item?.hotelId,
          reason: item?.availabilityStatus?.reason
        })))
        toast.error('Some hotel items are no longer available. Remove them before checkout.')
        return
      }

      // For hotels, navigate to client selection page if not all have clients
      const hotelItems = profile.filter(item => item.type !== 'experience')

      if (hotelItems.length === 0) {
        return
      }

      // Check if all hotels have selected clients
      const hotelWithoutClient = hotelItems.find(item => !selectedCustomerForItem[item.id])

      if (hotelWithoutClient) {
        // Instead of navigating away, show a descriptive toast to guide the user
        toast.error(`Please assign a client to "${hotelWithoutClient.title}" before continuing.`, {
          icon: '👤',
          style: {
            borderRadius: '12px',
            background: '#0f172a',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }
        });
      } else {
        // All hotels have clients, proceed to payment
        setCurrentStep(2)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  const handleNextStepExperiences = () => {
    if (profile && profile.length > 0) {
      const experienceItems = profile.filter(item => item.type === 'experience')

      if (experienceItems.length === 0) {
        toast.error('No experiences in cart')
        return
      }

      // Check if all experiences have selected clients
      const experienceWithoutClient = experienceItems.find(item => !selectedCustomerForItem[item.id])

      if (experienceWithoutClient) {
        // Show error message similar to hotels
        toast.error(`Please assign a client to "${experienceWithoutClient.title}" before continuing.`, {
          icon: '👤',
          style: {
            borderRadius: '12px',
            background: '#0f172a',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }
        });
        return
      }

      // Save to sessionStorage as backup
      experienceItems.forEach(item => {
        if (selectedCustomerForItem[item.id]) {
          sessionStorage.setItem(`selectedClient_${item.id}`, JSON.stringify(selectedCustomerForItem[item.id]))
        }
      })

      console.log('📦 Navigating to billing with data:', {
        experienceItems,
        selectedClientsForItems: selectedCustomerForItem
      })

      // All experiences have clients, navigate to billing page
      navigate('/cart/experience-billing', {
        state: {
          experienceItems,
          selectedClientsForItems: selectedCustomerForItem
        }
      })
    }
  }

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      // From payment step, go back to cart review
      setCurrentStep(1)
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // ADDED: Handle card number change with formatting
  const handleCardNumberChange = (value) => {
    // Remove all non-digit characters
    let formattedValue = value.replace(/\D/g, '')

    // Add spaces every 4 digits
    formattedValue = formattedValue.replace(/(.{4})/g, '$1 ').trim()

    // Limit to 19 characters (16 digits + 3 spaces)
    formattedValue = formattedValue.substring(0, 19)

    // Detect card type
    let cardType = ''
    const firstDigit = formattedValue.charAt(0)
    if (firstDigit === '4') {
      cardType = 'Visa'
    } else if (firstDigit === '5') {
      cardType = 'MasterCard'
    } else if (firstDigit === '3' && formattedValue.charAt(1) === '7') {
      cardType = 'American Express'
    } else if (firstDigit === '6') {
      cardType = 'Discover'
    }

    setNewCardDetails(prev => ({
      ...prev,
      cardNumber: formattedValue,
      cardType: cardType
    }))
  }

  // ADDED: Handle expiry change with formatting
  const handleExpiryChange = (value) => {
    // Keep a dedicated display string so caret/deletions behave predictably
    const cleaned = value.replace(/\D/g, '')

    // Update display input immediately
    if (cleaned.length === 0) {
      setExpiryInput('')
      setNewCardDetails(prev => ({ ...prev, expiryMonth: '', expiryYear: '' }))
      setExpiryError('')
      return
    }

    if (cleaned.length <= 2) {
      setExpiryInput(cleaned)
      setNewCardDetails(prev => ({ ...prev, expiryMonth: cleaned, expiryYear: '' }))
      // Validate month if at least 1-2 digits
      const monthNum = parseInt(cleaned, 10)
      if (!isNaN(monthNum) && (monthNum < 1 || monthNum > 12)) {
        setExpiryError('Invalid month (01-12)')
      } else {
        setExpiryError('')
      }
      return
    }

    // cleaned.length > 2
    const month = cleaned.slice(0, 2)
    const year = cleaned.slice(2, 4)
    const display = `${month}/${year}`
    setExpiryInput(display)
    setNewCardDetails(prev => ({ ...prev, expiryMonth: month, expiryYear: year }))

    // Validation: month must be between 01 and 12; year must not be in the past
    const monthNum = parseInt(month, 10)
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      setExpiryError('Invalid month (01-12)')
      return
    }

    if (year.length === 2) {
      const yearNum = parseInt(year, 10)
      const currentYear = new Date().getFullYear() % 100
      const currentMonth = new Date().getMonth() + 1

      if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
        setExpiryError('Card has expired')
        return
      }
    }

    setExpiryError('')
  }

  // ADDED: Format expiry for display
  const getFormattedExpiry = () => {
    return expiryInput || ''
  }

  const handleCompleteCheckout = async (paymentMethodOverride = null) => {
    const unavailableHotels = (profile || []).filter(
      (item) => item?.type !== 'experience' && item?.availabilityStatus?.isAvailable === false
    )
    if (unavailableHotels.length > 0) {
      console.warn('[UserCartPage] Blocking checkout confirmation due to unavailable hotels', unavailableHotels.map((item) => ({
        itemId: item?.id,
        hotelId: item?.hotelId,
        reason: item?.availabilityStatus?.reason
      })))
      setMessage({ type: 'error', text: 'Some hotel items are no longer available. Remove them before checkout.' })
      return
    }

    const activePaymentMethod = paymentMethodOverride || selectedPaymentMethod
    const effectiveUseNewCard = paymentMethodOverride ? false : useNewCard

    if (effectiveUseNewCard) {
      setMessage({
        type: 'error',
        text: 'Hotel checkout is Stripe-only. Please select a saved Stripe card (no manual card entry).'
      })
      return
    }

    if (!activePaymentMethod && !effectiveUseNewCard) {
      setMessage({ type: 'error', text: 'Please select a payment method' })
      return
    }

    // Pre-resolve BA flow for user visibility in confirmation modal
    try {
      const hotelItems = (profile || []).filter(item => item.type !== 'experience')
      if (hotelItems.length > 0) {
        const resolved = await checkoutService.resolveHotelCheckoutFlow({
          cartItems: hotelItems.map((item) => ({ ...item, cartItemId: item.id })),
          ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
        })
        if (resolved?.success && resolved?.data) {
          setCheckoutFlowPreview(resolved.data)
          const immediateItemsCount = resolved?.data?.flow?.immediateItemsCount || 0
          const hasStripeSavedMethod = !!activePaymentMethod?.stripePaymentMethodId

          if (immediateItemsCount > 0) {
            if (effectiveUseNewCard || !hasStripeSavedMethod) {
              setMessage({
                type: 'error',
                text: 'Immediate-payment items require selecting a saved Stripe card.'
              })
              return
            }
          }
        }
      }
    } catch (flowError) {
      console.warn('Could not pre-resolve checkout flow:', flowError)
      setCheckoutFlowPreview(null)
    }

    setShowConfirmation(true)
  }

  const processCheckout = async () => {
    const unavailableHotels = (profile || []).filter(
      (item) => item?.type !== 'experience' && item?.availabilityStatus?.isAvailable === false
    )
    if (unavailableHotels.length > 0) {
      console.warn('[UserCartPage] Blocking processCheckout due to unavailable hotels', unavailableHotels.map((item) => ({
        itemId: item?.id,
        hotelId: item?.hotelId,
        reason: item?.availabilityStatus?.reason
      })))
      setMessage({ type: 'error', text: 'Some hotel items are no longer available. Remove them before checkout.' })
      toast.error('Some hotel items are no longer available. Remove them before checkout.')
      return
    }

    setIsSaving(true)
    setShowConfirmation(false)

    try {
      if (useNewCard) {
        throw new Error('Hotel checkout is Stripe-only. Please select a saved Stripe card (no manual card entry).')
      }

      const idempotencyKey =
        (typeof window !== 'undefined' && window.crypto?.randomUUID)
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`

      // Filter to only process hotel items
      const hotelItems = profile.filter((item) => item.type !== 'experience')

      // Resolve BA payment flow server-side before final checkout
      // (phase 1 wiring: classification + metadata, no Stripe confirmation UI yet)
      let flowResolution = null
      try {
        const flowPayload = {
          cartItems: hotelItems.map((item) => ({
            ...item,
            cartItemId: item.id
          })),
          ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
        }
        const resolved = await checkoutService.resolveHotelCheckoutFlow(flowPayload)
        if (resolved?.success && resolved?.data) {
          flowResolution = resolved.data
          setCheckoutFlowPreview(resolved.data)
        }
      } catch (flowError) {
        console.warn('Could not resolve hotel checkout flow:', flowError)
      }

      // If there are immediate items, create Stripe PI for that immediate slice.
      // NOTE: in this phase we only create PI and pass metadata forward; UI confirmation flow follows next phase.
      let immediatePaymentIntents = []
      if ((flowResolution?.flow?.immediateItemsCount || 0) > 0) {
        if (!selectedPaymentMethod?.stripePaymentMethodId) {
          throw new Error('Immediate-payment items require selecting a saved Stripe card.')
        }
        const selectedPmId =
          selectedPaymentMethod?.id ||
          selectedPaymentMethod?._id ||
          selectedPaymentMethod?.stripePaymentMethodId ||
          selectedPaymentMethod?.cardId ||
          null

        const intentPayload = {
          cartItems: hotelItems.map((item) => ({
            ...item,
            cartItemId: item.id
          })),
          currency: hotelItems[0]?.currency || 'USD',
          paymentMethodId: selectedPmId,
          idempotencyKey,
          ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
        }

        const stripeIntentResult = await checkoutService.createHotelPaymentIntent(intentPayload)
        if (stripeIntentResult?.success) {
          const intents = Array.isArray(stripeIntentResult?.intents)
            ? stripeIntentResult.intents
            : (stripeIntentResult?.paymentIntentId ? [stripeIntentResult] : [])

          const stripe = await stripePromise
          if (!stripe) {
            throw new Error('Stripe failed to initialize for payment confirmation')
          }

          const cartItemTitleById = hotelItems.reduce((acc, it) => {
            const k = it?.id ? String(it.id) : null
            if (k) acc[k] = it?.title || it?.hotelDetails?.name || 'Hotel item'
            return acc
          }, {})

          for (const intent of intents) {
            const cartItemId = intent?.cartItemId ? String(intent.cartItemId) : null
            const itemTitle = cartItemId ? (cartItemTitleById[cartItemId] || `Cart item ${cartItemId}`) : 'Cart item'

            try {
              if (intent?.status === 'requires_action' && intent?.clientSecret) {
                const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret)
                if (error) {
                  throw new Error(error.message || '3DS confirmation failed')
                }
                if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                  throw new Error(`Payment not completed. Status: ${paymentIntent?.status || 'unknown'}`)
                }
                intent.status = paymentIntent.status
                intent.paymentIntentId = paymentIntent.id
              }

              if (intent?.status && intent.status !== 'succeeded') {
                throw new Error(`Immediate payment not successful. Status: ${intent.status}`)
              }
            } catch (e) {
              const msg = e?.message || 'Payment failed'
              throw new Error(`Payment failed for "${itemTitle}"${cartItemId ? ` (cartItemId: ${cartItemId})` : ''}: ${msg}`)
            }
          }

          immediatePaymentIntents = intents
          setStripeIntentPreview({
            ...stripeIntentResult,
            immediateIntentsCount: intents.length
          })
        }
      } else {
        // Scheduled-only checkout: no immediate PI is required at finalization time.
        // The backend scheduled-payment flow will use the saved/default Stripe method on file.
        setStripeIntentPreview(null)
      }

      // Calculate summary only for hotels
      const getItemProcessingFees = (item) => {
        const explicit =
          Number(
            item?.processingFees?.totalProcessingFees ??
              item?.priceDetails?.processingFees?.totalProcessingFees ??
              item?.rateDetails?.processingFees?.totalProcessingFees ??
              item?.priceDetails?.additionalFees
          ) || 0
        const payable = Number(getHotelCartLinePayable(item) || 0)
        const base = Number(item?.priceDetails?.baseRate || 0)
        const taxAmount = Number(item?.priceDetails?.tax || 0)
        const markup = Number(item?.agentCommission?.markupAmount || 0)
        const derived = payable - base - taxAmount - markup

        if (Number.isFinite(explicit) && explicit > 0) {
          if (Number.isFinite(derived) && derived > 0 && Math.abs(derived - explicit) > 0.02) {
            return derived
          }
          return explicit
        }

        return Number.isFinite(derived) && derived > 0 ? derived : 0
      }

      const hotelSummary = {
        subtotal: hotelItems.reduce((sum, item) => sum + (item.priceDetails?.baseRate || 0), 0),
        tax: hotelItems.reduce((sum, item) => sum + (item.priceDetails?.tax || 0), 0),
        fees: hotelItems.reduce((sum, item) => sum + getItemProcessingFees(item), 0),
        total:
          qaPromoPreview?.checkoutType === 'hotel' && qaPromoPreview.grandTotal != null
            ? qaPromoPreview.grandTotal
            : hotelItems.reduce((sum, item) => sum + getHotelCartLinePayable(item), 0),
        currency: hotelItems[0]?.currency || 'USD'
      }

      // Only label the *whole* checkout as off-market when every hotel line is off-market.
      // Mixed carts use HOTEL_STAY at the request level so finance/logging stay honest; the backend
      // still derives OFF_MARKET_STAY + OFFMKT-* per line from cart flags for each booking row.
      const allHotelLinesAreOffMarket =
        hotelItems.length > 0 && hotelItems.every((item) => item?.isOffMarketStay === true)
      const referenceType = allHotelLinesAreOffMarket ? 'OFF_MARKET_STAY' : 'HOTEL_STAY'
      const referenceValue = allHotelLinesAreOffMarket
        ? `OFFMKT-${idempotencyKey}`
        : `HOTEL-${idempotencyKey}`

      // Prepare the checkout data in the format expected by your service
      const checkoutData = {
        cartItems: hotelItems.map(item => {
          const selectedCustomer = selectedCustomerForItem[item.id];

          return {
            type: 'hotel',
            cartItemId: item.id,
            hotelId: item.hotelId || item.propertyId,
            roomType: item.roomType || item.bedType?.type,
            ratePlanId: item.ratePlanId,
            checkInDate: item.checkInDate,
            checkOutDate: item.checkOutDate,
            nights: item.nights,
            adults: item.maxAdults,
            children: item.maxChildren || 0,
            priceDetails: item.priceDetails,
            totalAmount: getHotelChargedDisplay(item),
            agentCommission: item.agentCommission || undefined,
            currency: item.currency || hotelSummary.currency,
            customerDetails: selectedCustomer ? {
              customerId: selectedCustomer.id || selectedCustomer._id,
              firstName: selectedCustomer.firstName,
              lastName: selectedCustomer.lastName,
              email: selectedCustomer.email,
              phone: selectedCustomer.phone,
              address: selectedCustomer.address,
              postalCode: selectedCustomer.postalCode,
              state: selectedCustomer.state,
              city: selectedCustomer.city,
              country: selectedCustomer.country,
              specialRequests: selectedCustomer.specialRequests || ''
            } : null
          }
        }),
        cardDetails: {
          id: selectedPaymentMethod.id || selectedPaymentMethod._id,
          cardId: selectedPaymentMethod.cardId || selectedPaymentMethod.stripePaymentMethodId || null,
          stripePaymentMethodId: selectedPaymentMethod.stripePaymentMethodId || null,
          stripeCustomerId: selectedPaymentMethod.stripeCustomerId || null,
          last4: selectedPaymentMethod.last4,
          brand: selectedPaymentMethod.brand,
          expMonth: selectedPaymentMethod.expMonth,
          expYear: selectedPaymentMethod.expYear,
          funding: selectedPaymentMethod.funding,
          billingAddress: {
            street: billingAddress.street,
            city: billingAddress.city,
            stateProvince: billingAddress.stateProvince,
            postalCode: billingAddress.postalCode,
            countryCode: billingAddress.countryCode
          },
          isNewCard: false
        },
        finalPriceUsd: hotelSummary.total,
        currency: hotelSummary.currency,
        paymentFlow: flowResolution?.flow || null,
        paymentSplit: flowResolution?.split || null,
        idempotencyKey,
        checkoutTraceId: idempotencyKey,
        bookingReference: {
          value: referenceValue,
          type: referenceType,
          source: 'frontend_checkout'
        },
        immediatePaymentIntents: immediatePaymentIntents?.length
          ? immediatePaymentIntents.map((i) => ({
            cartItemId: i.cartItemId || null,
            paymentIntentId: i.paymentIntentId || i.id || null,
            amount: i.amount || null,
            currency: i.currency || null
          }))
          : null,
        billingAddress: {
          street: billingAddress.street,
          city: billingAddress.city,
          stateProvince: billingAddress.stateProvince,
          postalCode: billingAddress.postalCode,
          countryCode: billingAddress.countryCode
        },
        ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
      }

      // Validate and process checkout using your service
      const response = await checkoutService.processFinalCheckout(checkoutData)

      setMessage({ type: 'success', text: 'Booking completed successfully!' })
      toast.success(`Booking completed successfully!`)

      // Clear the cart after successful checkout
      setTimeout(() => {
        clearCartCount()
        setProfile([])
        setSelectedCustomerForItem({})
        navigate('/user-bookings')
      }, 2000)

    } catch (error) {
      console.error('Checkout failed:', error)
      setMessage({ type: 'error', text: error.message || 'Checkout failed. Please try again.' })
      toast.error(error.message || 'Checkout failed. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClientRegistered = (newClient, itemId) => {
    if (itemId) {
      handleCustomerSelect(itemId, newClient._id, newClient)
    }
  }

  if (isLoading) {
    return (
      <MainLayout>
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f7f7f7' }}>
        <LoadingSpinner size="xl" />
      </div>
      </MainLayout>
    )
  }

  const summary = calculateSummary()
  const paymentFlowByItemId = (checkoutFlowPreview?.flow?.itemFlows || []).reduce((acc, itemFlow) => {
    const key = itemFlow?.cartItemId ? String(itemFlow.cartItemId) : null
    if (!key) return acc
    acc[key] = itemFlow?.requiresImmediatePayment ? 'immediate' : 'scheduled'
    return acc
  }, {})
  const unavailableHotelItems = (profile || []).filter(
    (item) => item?.type !== 'experience' && item?.availabilityStatus?.isAvailable === false
  )
  const hasUnavailableHotelItems = unavailableHotelItems.length > 0
  const steps = [
    { number: 1, title: 'Cart Review', completed: currentStep > 1 },
    { number: 2, title: 'Payment', completed: false }
  ]

  return (
    <MainLayout>
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200" style={{ borderTopWidth: 2, borderTopColor: '#111827' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 pt-5 pb-2">
            <button type="button" onClick={() => navigate('/dashboard')}
              className="text-[9px] font-poppinsLight uppercase tracking-[0.3em] text-gray-400 hover:text-gray-700 transition-colors duration-200">
              Dashboard
            </button>
            <span className="text-gray-300 text-[10px]">/</span>
            <span className="text-[9px] font-poppinsRegular uppercase tracking-[0.3em] text-gray-900">Cart</span>
          </div>

          {/* Title row */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-5">
            <div>
              <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 leading-tight"
                style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.2rem)' }}>
                Your Cart
              </h1>
              {profile && profile.length > 0 && (
                <p className="text-[9px] font-poppinsLight uppercase tracking-[0.3em] text-gray-400 mt-1">
                  {profile.length} item{profile.length > 1 ? 's' : ''} · Ready for checkout
                </p>
              )}
            </div>

            {/* Quick browse links */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[9px] font-poppinsLight uppercase tracking-[0.25em] text-gray-300 mr-2 hidden sm:block">Add more:</span>
              <button type="button" onClick={() => navigate('/hotels')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 text-gray-500 text-[9px] font-poppinsRegular uppercase tracking-[0.2em] hover:border-gray-900 hover:text-gray-900 transition-all duration-200">
                <Hotel className="h-3 w-3" /> Hotels
              </button>
              <button type="button" onClick={() => navigate('/experiences')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 text-gray-500 text-[9px] font-poppinsRegular uppercase tracking-[0.2em] hover:border-gray-900 hover:text-gray-900 transition-all duration-200">
                <Compass className="h-3 w-3" /> Experiences
              </button>
              <button type="button" onClick={() => navigate('/private-jets')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 text-gray-500 text-[9px] font-poppinsRegular uppercase tracking-[0.2em] hover:border-gray-900 hover:text-gray-900 transition-all duration-200">
                <Plane className="h-3 w-3" /> Private Jets
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-0">
            {steps.map((step, index) => {
              const isActive = currentStep === step.number;
              const isCompleted = step.completed;
              return (
                <div key={step.number} className="flex items-center flex-1 last:flex-none">
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={false}
                      animate={{
                        backgroundColor: isActive || isCompleted ? '#111827' : '#ffffff',
                        borderColor: isActive || isCompleted ? '#111827' : '#d1d5db',
                      }}
                      className="flex items-center justify-center w-7 h-7 border shrink-0 transition-all duration-500"
                    >
                      {isCompleted ? (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                          <CheckCircle className="h-3.5 w-3.5 text-white" />
                        </motion.div>
                      ) : (
                        <span className={`text-[10px] font-poppinsRegular font-bold tracking-[0.1em] ${isActive ? 'text-white' : 'text-gray-400'}`}>
                          {step.number}
                        </span>
                      )}
                    </motion.div>
                    <p className={`text-[9px] font-poppinsRegular uppercase tracking-[0.28em] transition-all duration-300 ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-4 h-px bg-gray-200 overflow-hidden">
                      <motion.div
                        initial={false}
                        animate={{ width: isCompleted ? '100%' : '0%' }}
                        className="h-full bg-gray-900"
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {message.text && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className={`p-4 border text-sm font-poppinsLight tracking-wide ${message.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
            {message.text}
          </div>
        </div>
      )}
      {hasUnavailableHotelItems && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="p-4 bg-amber-50 text-amber-800 border border-amber-200 text-sm font-poppinsLight tracking-wide">
            {unavailableHotelItems.length} hotel item{unavailableHotelItems.length > 1 ? 's are' : ' is'} no longer available. Remove unavailable items to continue checkout.
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ backgroundColor: '#f7f7f7', minHeight: '50vh' }}>
        {currentStep === 1 && (
          <CartReviewStep
            profile={profile}
            selectedCustomerForItem={selectedCustomerForItem}
            agentCustomerDetails={agentCustomerDetails}
            summary={summary}
            deleteCartItemLoading={deleteCartItemLoading}
            deleteAllCartDeleteLoading={deleteAllCartDeleteLoading}
            onDeleteItem={handleDeleteItem}
            onClearAll={handleClearAll}
            onCustomerSelect={handleCustomerSelect}
            onNextStep={handleNextStep}
            onNextStepExperiences={handleNextStepExperiences}
            onNavigateHotels={() => navigate('/hotels')}
            onShowClientRegistration={(itemId) => {
              sessionStorage.setItem('currentCartItemId', itemId)
              setShowClientRegistration(true)
            }}
            agentMarkupSettings={agentMarkupSettings}
            paymentFlowByItemId={paymentFlowByItemId}
          />
        )}

        {currentStep === 2 && (
          <Elements stripe={stripePromise}>
            <PaymentStep
              profile={profile?.filter(item => item.type !== 'experience') || []}
              selectedCustomerForItem={selectedCustomerForItem}
              userPaymentDetails={userPaymentDetails}
              selectedPaymentMethod={selectedPaymentMethod}
              useNewCard={useNewCard}
              newCardDetails={newCardDetails}
              billingAddress={billingAddress}
              summary={(() => {
                const hotelItems = profile?.filter(item => item.type !== 'experience') || []
                if (hotelItems.length === 0) return null
                let subtotal = 0
                let tax = 0
                let fees = 0
                let total = 0
                let markupTotal = 0
                const currency = hotelItems[0]?.currency || 'USD'
                const getItemProcessingFees = (item) => {
                  const explicit =
                    Number(
                      item?.processingFees?.totalProcessingFees ??
                        item?.priceDetails?.processingFees?.totalProcessingFees ??
                        item?.rateDetails?.processingFees?.totalProcessingFees ??
                        item?.priceDetails?.additionalFees
                    ) || 0
                  const payable = Number(getHotelCartLinePayable(item) || 0)
                  const base = Number(item?.priceDetails?.baseRate || 0)
                  const taxAmount = Number(item?.priceDetails?.tax || 0)
                  const markup = Number(item?.agentCommission?.markupAmount || 0)
                  const derived = payable - base - taxAmount - markup

                  if (Number.isFinite(explicit) && explicit > 0) {
                    if (Number.isFinite(derived) && derived > 0 && Math.abs(derived - explicit) > 0.02) {
                      return derived
                    }
                    return explicit
                  }

                  return Number.isFinite(derived) && derived > 0 ? derived : 0
                }

                hotelItems.forEach((item) => {
                  subtotal += item.priceDetails?.baseRate || 0
                  tax += item.priceDetails?.tax || 0
                  fees += getItemProcessingFees(item)
                  total += getHotelChargedDisplay(item)
                  markupTotal += item.agentCommission?.markupAmount || 0
                })
                return { subtotal, tax, fees, total, currency, markupTotal }
              })()}
              isSaving={isSaving}
              onPreviousStep={handlePreviousStep}
              onPaymentMethodSelect={handlePaymentMethodSelect}
              onUseNewCardChange={setUseNewCard}
              onNewCardChange={setNewCardDetails}
              onBillingAddressChange={setBillingAddress}
              onCardNumberChange={handleCardNumberChange}
              onExpiryChange={handleExpiryChange}
              getFormattedExpiry={getFormattedExpiry}
              expiryError={expiryError}
              onCompleteCheckout={handleCompleteCheckout}
              onNavigateHotels={() => navigate('/hotels')}
              paymentFlowByItemId={paymentFlowByItemId}
              onDeleteSavedPaymentMethod={handleDeleteSavedPaymentMethod}
              getHotelLinePayableDisplay={getHotelChargedDisplay}
              qaPromoApplySection={
                QA_PROMO_UI_ENABLED && (profile?.filter((i) => i.type !== 'experience')?.length > 0) ? (
                  <PromoCodeApply
                    checkoutType="hotel"
                    disabled={isSaving}
                    onApplied={handleQaPromoApplied}
                    onCleared={handleQaPromoCleared}
                  />
                ) : null
              }
            />
          </Elements>
        )}

        {/* Confirmation Modal */}
        {showConfirmation && (() => {
          // Calculate hotel-only summary for confirmation modal
          const hotelItems = profile?.filter(item => item.type !== 'experience') || []
          const flowByItemId = (checkoutFlowPreview?.flow?.itemFlows || []).reduce((acc, itemFlow) => {
            const key = itemFlow?.cartItemId ? String(itemFlow.cartItemId) : null
            if (!key) return acc
            acc[key] = itemFlow?.requiresImmediatePayment ? 'immediate' : 'scheduled'
            return acc
          }, {})

          const immediateItems = hotelItems.filter((item) => flowByItemId[String(item.id)] === 'immediate')
          const scheduledItems = hotelItems.filter((item) => flowByItemId[String(item.id)] === 'scheduled')

          const getItemProcessingFees = (item) => {
            const explicit =
              Number(
                item?.processingFees?.totalProcessingFees ??
                  item?.priceDetails?.processingFees?.totalProcessingFees ??
                  item?.rateDetails?.processingFees?.totalProcessingFees ??
                  item?.priceDetails?.additionalFees
              ) || 0
            const payable = Number(getHotelCartLinePayable(item) || 0)
            const base = Number(item?.priceDetails?.baseRate || 0)
            const taxAmount = Number(item?.priceDetails?.tax || 0)
            const markup = Number(item?.agentCommission?.markupAmount || 0)
            const derived = payable - base - taxAmount - markup

            if (Number.isFinite(explicit) && explicit > 0) {
              if (Number.isFinite(derived) && derived > 0 && Math.abs(derived - explicit) > 0.02) {
                return derived
              }
              return explicit
            }

            return Number.isFinite(derived) && derived > 0 ? derived : 0
          }

          const buildBucketBreakdown = (items) => {
            return items.reduce((acc, item) => {
              acc.base += Number(item?.priceDetails?.baseRate || 0)
              acc.tax += Number(item?.priceDetails?.tax || 0)
              acc.processingFees += getItemProcessingFees(item)
              acc.total += Number(getHotelChargedDisplay(item) || 0)
              return acc
            }, { base: 0, tax: 0, processingFees: 0, total: 0 })
          }

          const immediateBreakdown = buildBucketBreakdown(immediateItems)
          const scheduledBreakdown = buildBucketBreakdown(scheduledItems)

          const hotelSummary = hotelItems.length > 0 ? {
            count: hotelItems.length,
            total: hotelItems.reduce((sum, item) => sum + getHotelChargedDisplay(item), 0),
            currency: hotelItems[0]?.currency || 'USD'
          } : null

          return createPortal(
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] p-4 sm:px-6 sm:pb-6 sm:pt-24 flex items-end sm:items-start justify-center">
              <div className="bg-white w-full sm:max-w-md shadow-2xl transform transition-all duration-300 scale-100 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-start space-x-4 p-4 sm:p-6 border-b border-gray-200" style={{ borderTopWidth: 2, borderTopColor: '#111827' }}>
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-amber-50 border border-amber-200 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-base">Confirm Checkout</h3>
                    <p className="text-[11px] font-poppinsLight text-gray-500 mt-1 tracking-wide">Please review your booking details</p>
                  </div>
                </div>

                <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
                  <div className="bg-gray-50 border border-gray-200 p-4">
                    <h4 className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em] text-gray-500 mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Items in Cart:</span>
                        <span className="font-medium">{hotelSummary?.count || 0}</span>
                      </div>
                      {hotelItems.length > 0 && (() => {
                        const om = hotelItems.filter((i) => i?.isOffMarketStay === true).length
                        const gds = hotelItems.length - om
                        const label =
                          om > 0 && gds > 0
                            ? `${om} off-market · ${gds} GDS`
                            : om > 0
                              ? `${om} off-market catalogue`
                              : `${gds} GDS hotel`
                        return (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Stay lines:</span>
                            <span className="font-medium text-right text-slate-800">{label}</span>
                          </div>
                        )
                      })()}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Total Amount:</span>
                        <span className="font-bold text-slate-900">{hotelSummary?.currency} {hotelSummary?.total?.toFixed(2) || '0.00'}</span>
                      </div>
                      {checkoutFlowPreview?.flow?.flowType && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Flow:</span>
                          <span className="font-semibold uppercase text-slate-900">
                            {checkoutFlowPreview.flow.flowType.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                      {(stripeIntentPreview?.paymentIntentId || stripeIntentPreview?.immediateIntentsCount) && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Stripe Immediate PI:</span>
                          <span className="font-semibold text-slate-900">
                            {stripeIntentPreview.immediateIntentsCount
                              ? `${stripeIntentPreview.immediateIntentsCount} intents • ${stripeIntentPreview.amount?.toFixed?.(2) || stripeIntentPreview.amount} ${stripeIntentPreview.currency || ''}`
                              : `${stripeIntentPreview.amount?.toFixed?.(2) || stripeIntentPreview.amount} ${stripeIntentPreview.currency || ''}`
                            }
                          </span>
                        </div>
                      )}
                      {(immediateItems.length > 0 || scheduledItems.length > 0) && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Immediate Charge:</span>
                            <span className="font-semibold text-slate-900">
                              {hotelSummary?.currency} {Number(immediateBreakdown.total || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Scheduled Charge:</span>
                            <span className="font-semibold text-slate-900">
                              {hotelSummary?.currency} {Number(scheduledBreakdown.total || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-slate-200">
                            <span className="text-slate-600">Total (Immediate + Scheduled):</span>
                            <span className="font-bold text-slate-900">
                              {hotelSummary?.currency}{' '}
                              {Number((immediateBreakdown.total || 0) + (scheduledBreakdown.total || 0)).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {(immediateItems.length > 0 || scheduledItems.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-3 text-xs">
                        {immediateItems.length > 0 && (
                          <div className="border border-rose-200 bg-rose-50 p-2.5 space-y-1.5">
                            <p className="font-bold uppercase tracking-wide text-rose-700">
                              Immediate Bucket ({immediateItems.length} item{immediateItems.length > 1 ? 's' : ''})
                            </p>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Base</span>
                              <span className="font-medium text-slate-900">{hotelSummary?.currency} {immediateBreakdown.base.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Tax and fees</span>
                              <span className="font-medium text-slate-900">
                                {hotelSummary?.currency} {(Number(immediateBreakdown.tax || 0) + Number(immediateBreakdown.processingFees || 0)).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between pt-1 border-t border-rose-200">
                              <span className="font-semibold text-rose-700">Charge Now</span>
                              <span className="font-bold text-slate-900">{hotelSummary?.currency} {immediateBreakdown.total.toFixed(2)}</span>
                            </div>
                          </div>
                        )}

                        {scheduledItems.length > 0 && (
                          <div className="border border-slate-800 bg-slate-900 p-2.5 space-y-1.5">
                            <p className="font-bold uppercase tracking-wide text-white">
                              Scheduled Bucket ({scheduledItems.length} item{scheduledItems.length > 1 ? 's' : ''})
                            </p>
                            <div className="flex justify-between">
                              <span className="text-blue-50">Base</span>
                              <span className="font-medium text-white">{hotelSummary?.currency} {scheduledBreakdown.base.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-blue-50">Tax and fees</span>
                              <span className="font-medium text-white">
                                {hotelSummary?.currency} {(Number(scheduledBreakdown.tax || 0) + Number(scheduledBreakdown.processingFees || 0)).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between pt-1 border-t border-slate-700">
                              <span className="font-semibold text-white">Charge Later</span>
                              <span className="font-bold text-white">{hotelSummary?.currency} {scheduledBreakdown.total.toFixed(2)}</span>
                            </div>
                            <p className="pt-1 text-[11px] text-blue-50 font-medium">
                              Scheduled charges are collected later (one Stripe payment per scheduled item).
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 border border-gray-200 p-4">
                    <h4 className="text-[10px] font-poppinsRegular uppercase tracking-[0.25em] text-gray-500 mb-3">Per-Item Stripe Timing</h4>
                    <div className="space-y-2">
                      {hotelItems.map((item) => {
                        const mode = flowByItemId[String(item.id)]
                        const isImmediate = mode === 'immediate'
                        const payable = Number(getHotelChargedDisplay(item) || 0)
                        const computeScheduledDueAt = () => {
                          const cd = item?.cancellationDeadline ? new Date(item.cancellationDeadline) : null
                          if (cd && !Number.isNaN(cd.getTime())) {
                            return new Date(cd.getTime() - 72 * 60 * 60 * 1000)
                          }
                          const checkIn = item?.checkInDate ? new Date(item.checkInDate) : null
                          if (checkIn && !Number.isNaN(checkIn.getTime())) {
                            return new Date(checkIn.getTime() - 72 * 60 * 60 * 1000)
                          }
                          return null
                        }
                        const dueAt = mode === 'scheduled' ? computeScheduledDueAt() : null
                        const badgeClass = isImmediate
                          ? 'bg-rose-50 border-rose-200 text-rose-700'
                          : mode === 'scheduled'
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : 'bg-amber-50 border-amber-200 text-amber-700'
                        const badgeText = isImmediate
                          ? 'Immediate Stripe'
                          : mode === 'scheduled'
                            ? 'Scheduled Stripe'
                            : 'Resolving...'

                        return (
                          <div key={item.id} className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm text-slate-700 truncate">{item.title}</p>
                              {(() => {
                                const s = getHotelStaySourcePresentation(item)
                                return (
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mt-0.5 truncate">
                                    {s.pillText}
                                    {s.detailText ? (
                                      <span className="ml-1 font-semibold normal-case text-slate-500">· {s.detailText}</span>
                                    ) : null}
                                  </p>
                                )
                              })()}
                              <p className="text-[11px] text-slate-500 font-medium tabular-nums">
                                {hotelSummary?.currency} {payable.toFixed(2)}
                              </p>
                              {mode === 'scheduled' ? (
                                <p className="text-[11px] text-emerald-700/80 font-semibold">
                                  Scheduled for:{' '}
                                  {dueAt
                                    ? dueAt.toLocaleString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                    : 'TBD'}
                                </p>
                              ) : mode === 'immediate' ? (
                                <p className="text-[11px] text-rose-700/80 font-semibold">Charges now</p>
                              ) : null}
                            </div>
                            <span className={`shrink-0 border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${badgeClass}`}>
                              {badgeText}
                            </span>
                          </div>
                        )
                      })}
                      {(immediateItems.length > 0 || scheduledItems.length > 0) && (
                        <div className="pt-2 mt-2 border-t border-slate-200 text-[11px] text-slate-600 font-medium">
                          Immediate: {immediateItems.length} item(s) • Scheduled: {scheduledItems.length} item(s)
                        </div>
                      )}
                    </div>
                  </div>

                <div className="bg-blue-50 border border-blue-200 p-4">
                  <div className="flex items-start space-x-3">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-800 font-poppinsLight">
                      <p className="font-poppinsRegular uppercase tracking-[0.2em] text-[10px] mb-1.5">Important Information</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>This action cannot be undone</li>
                        <li>Booking confirmation will be sent via email</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 p-4 sm:p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowConfirmation(false)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all duration-200 text-[10px] font-poppinsRegular uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={processCheckout}
                  disabled={isSaving}
                  className="flex-1 px-4 py-3 bg-gray-900 hover:bg-black text-white transition-all duration-200 text-[10px] font-poppinsRegular uppercase tracking-[0.2em] disabled:opacity-50 relative overflow-hidden"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Confirm & Pay
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
      </main>

      {/* Modals */}
      <AddCardModal
        isOpen={isAddCardModalOpen}
        onClose={() => setIsAddCardModalOpen(false)}
        onCardAdded={handleCardAdded}
      />

      <ClientRegistrationModal
        isOpen={showClientRegistration}
        onClose={() => {
          setShowClientRegistration(false)
          sessionStorage.removeItem('currentCartItemId')
        }}
        onRegister={async (clientData) => {
          try {
            const newClient = await handleRegisterClient(clientData)
            const currentItemId = sessionStorage.getItem('currentCartItemId')
            if (currentItemId) {
              handleClientRegistered(newClient, currentItemId)
              sessionStorage.removeItem('currentCartItemId')
            }
            setShowClientRegistration(false)
            setCustomerDetails({
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              address: '',
              postalCode: '',
              state: '',
              city: '',
              country: '',
              specialRequests: ''
            })
          } catch (error) {
            // Error handling is done in handleRegisterClient
          }
        }}
        isRegistering={isRegistering}
        customerDetails={customerDetails}
        setCustomerDetails={setCustomerDetails}
      />
    </MainLayout>
  )
}

export default UserCartPage